import type { Rect } from '@/lib/shapes/shape-coordinates'
import {
  HANDLE_BORDER_WIDTH,
  SELECTION_COLOR,
  SELECTION_DASH_ARRAY,
} from '@/components/app/editor/selection-chrome/selection-chrome-constants'

/**
 * Transient drag rectangles: marquee selection, marquee zoom, and the text drag-to-create preview.
 *
 * All three were `border: 1px dashed` on divs inside the zoom-scaled canvas, so the outline weight
 * *and* the dash period were document pixels — a hairline with imperceptible dashes when zoomed out,
 * a thick border with long dashes when zoomed in. Here they are real screen pixels, and the dash
 * pattern is the same `SELECTION_DASH_ARRAY` the selection frame uses, so a marquee and a
 * multi-select frame finally read as the same language.
 *
 * Rects arrive in document coordinates and are projected here. Unlike chrome, these come straight
 * from `center-canvas` state as props rather than through the shape-manager poll — the drag that
 * produces them is already re-rendering that component every frame.
 */

const VARIANT_STYLE = {
  /** Selection marquee — same accent as the selection frame, with a light wash. */
  marqueeSelect: { stroke: SELECTION_COLOR, fill: 'rgba(2, 132, 199, 0.08)' },
  /** Zoom-to-region marquee — amber, to read as a viewport action rather than a selection. */
  marqueeZoom: { stroke: 'var(--color-amber-500, #f59e0b)', fill: 'rgba(245, 158, 11, 0.12)' },
  /** Drag-to-create preview — outline only, so it reads as a shape about to exist. */
  createPreview: { stroke: SELECTION_COLOR, fill: 'none' },
} as const

export type DragRectVariant = keyof typeof VARIANT_STYLE

export type DragRectLayerProps = {
  /** Document-space rects, in draw order. `null` entries are skipped. */
  rects: Array<{ variant: DragRectVariant; rect: Rect | null }>
  /** Current canvas zoom. */
  scale: number
}

export function DragRectLayer({ rects, scale }: DragRectLayerProps) {
  const visible = rects.filter(
    (entry): entry is { variant: DragRectVariant; rect: Rect } => entry.rect != null
  )

  if (visible.length === 0) return null

  return (
    <g data-drag-rect-layer="">
      {visible.map(({ variant, rect }) => {
        const style = VARIANT_STYLE[variant]

        return (
          <rect
            key={variant}
            x={rect.x * scale}
            y={rect.y * scale}
            width={rect.width * scale}
            height={rect.height * scale}
            fill={style.fill}
            stroke={style.stroke}
            strokeWidth={HANDLE_BORDER_WIDTH}
            strokeDasharray={SELECTION_DASH_ARRAY}
            style={{ pointerEvents: 'none' }}
          />
        )
      })}
    </g>
  )
}

export default DragRectLayer
